import { kebabCase } from 'lodash';
import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  ApprovalStatus,
  getAdminTaskManagerPath,
  getAdminTaskPath,
  getApprovalDeskPath,
  getProcessManagerPath,
  getTaskManagerPath,
  getTaskPath,
} from '@css-xp/apd-common';
import { useSidebar } from '@css-xp/base';
import { COMPLETED_TASKS_TAB, OPEN_TASKS_TAB, OPEN_TASK_STATUS } from '../constants/task';
import { SIDEBAR_TASK_DETAIL } from '../panels';

const getHash = (status: ApprovalStatus | undefined | null) => {
  return status
    ? OPEN_TASK_STATUS.includes(status)
      ? `#${kebabCase(OPEN_TASKS_TAB)}`
      : `#${kebabCase(COMPLETED_TASKS_TAB)}`
    : '';
};

export const useTaskManager = () => {
  const { openSidebar, closeSidebar, isSidebarOpen } = useSidebar();
  const [isTaskOpen, setTaskOpen] = React.useState(false);
  const history = useHistory();
  const location = useLocation();
  const isApprovalDeskPage = useMemo(() => {
    return location?.pathname?.includes(getApprovalDeskPath());
  }, [location]);

  React.useEffect(() => {
    if (isTaskOpen && !isSidebarOpen) {
      closeTask();
      setTaskOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidebarOpen, isTaskOpen]);

  const openTaskSidebar = React.useCallback(
    ({ taskId, refetch, refetchAll }: { taskId: string; refetch?: () => void; refetchAll?: () => void }) => {
      openSidebar({
        sidebarType: SIDEBAR_TASK_DETAIL,
        sidebarOptions: {
          isOverlay: true,
          sidebarWidth: '40%',
        },
        payload: {
          taskId,
          refetch,
          refetchAll,
          closeSidebar,
        },
      });
      if (isApprovalDeskPage) {
        setTaskOpen(true);
      }
    },
    [openSidebar]
  );

  const closeTaskSidebar = React.useCallback(() => {
    if (isSidebarOpen) {
      closeSidebar();
    }
  }, [closeSidebar, isSidebarOpen]);

  const openTask = React.useCallback(
    ({
      taskId,
      status,
      refetch,
      refetchAll,
    }: {
      taskId: string;
      status: ApprovalStatus | undefined | null;
      refetch?: () => void;
      refetchAll?: () => void;
    }) => {
      if (isApprovalDeskPage) {
        const isSuperUser = location?.pathname?.includes(getProcessManagerPath());
        const params: any = { pathname: isSuperUser ? getAdminTaskPath(taskId) : getTaskPath(taskId) };
        const hash = getHash(status);
        if (hash && location.hash) {
          params['hash'] = hash;
        }
        history.push(params);
      } else {
        openTaskSidebar({ taskId, refetch, refetchAll });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [location]
  );

  const closeTask = React.useCallback(() => {
    const isSuperUser = !!location?.pathname?.includes(getProcessManagerPath());
    const params: any = { pathname: isSuperUser ? getAdminTaskManagerPath() : getTaskManagerPath() };
    if (location.hash) {
      params['hash'] = location.hash;
    }
    history.push(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  return { openTaskSidebar, openTask, closeTaskSidebar, closeTask };
};
